import { useEffect, useRef } from "react";

interface UseSheetBackNavigationParams {
    isOpen: boolean;
    onRequestClose: () => void;
}

/**
 * Intercepts the browser back action while a sheet/modal is open.
 *
 * When `isOpen` becomes true, pushes a synthetic history entry so the first
 * back press closes the sheet instead of navigating away. When the sheet
 * closes by any other means (UI button), the synthetic entry is cleaned up
 * automatically so normal navigation is not disrupted.
 *
 * The `onRequestClose` callback is stored in a ref so it never needs to be
 * listed as an effect dependency — this prevents listener churn on re-renders.
 */
export const useSheetBackNavigation = ({
    isOpen,
    onRequestClose,
}: UseSheetBackNavigationParams) => {
    const onRequestCloseRef = useRef(onRequestClose);
    const isProgrammaticBackRef = useRef(false);

    // Keep callback ref in sync without triggering the main effect
    useEffect(() => {
        onRequestCloseRef.current = onRequestClose;
    });

    useEffect(() => {
        if (!isOpen) return;

        // Push a sentinel history entry so back button lands here first
        window.history.pushState(
            { ...window.history.state, __sheetBackInterceptor: true },
            "",
            window.location.href,
        );

        const handlePopState = () => {
            if (isProgrammaticBackRef.current) {
                isProgrammaticBackRef.current = false;
                return;
            }
            onRequestCloseRef.current();
        };

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
            // Sheet closed via UI (not back): remove the sentinel entry so
            // the next back press navigates normally instead of landing on it.
            if (window.history.state?.__sheetBackInterceptor) {
                isProgrammaticBackRef.current = true;
                window.history.back();
            }
        };
    }, [isOpen]);
};
